import React, { useCallback, useEffect, useRef, useState } from "react";
import AvatarGroup from "@mui/material/AvatarGroup";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import LinearProgress from "@mui/material/LinearProgress";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import LoadingButton from "@mui/lab/LoadingButton";
import ArchiveRoundedIcon from "@mui/icons-material/ArchiveRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import { workspacesApi } from "../../actions";
import { brand, notice } from "../../theme/businessFlowsTheme";
import { canPlatform } from "../../utils/permissions";
import { SHOW_DEVELOPER_API } from "../../features";
import UsersTab from "./UsersTab";
// Groups no longer decide anything (product owner, 28 Sep 2026): a view
// belongs to one database and is given to people directly, on the Views tab.
// The screen is kept, commented out, as this codebase keeps what it retires.
// import GroupsTab from "./GroupsTab";
import ViewsTab from "./ViewsTab";
import DatabasesTab from "./DatabasesTab";
import PlanTab from "./PlanTab";
import ApiKeysTab from "./ApiKeysTab";
import ActivityTab from "./ActivityTab";
import { ConfirmDialog, Refusal, Toast, focusRing, quietDanger } from "./WorkspaceUi";
import { STATUS_LABELS, fullName, initials, nameInitials, planLabel, seatText } from "./workspaceFormat";

const TABS = [
  { key: "users", label: "Users" },
  // Retired 28 Sep 2026 - what Groups did is on Views now, where a view
  // lists its database and the people who hold it.
  // { key: "groups", label: "Groups" },
  { key: "views", label: "Views" },
  { key: "databases", label: "Databases" },
  { key: "plan", label: "Plan" },
  // Lean UI for the trade fair: SHOW_DEVELOPER_API=false hides API keys.
  // Flip it in ../../features to bring the tab back.
  ...(SHOW_DEVELOPER_API ? [{ key: "api", label: "API keys" }] : []),
  { key: "activity", label: "Activity" },
];

// One workspace: its people, views, databases and plan. A view belongs to one
// database and is given to people directly, so Views is where access is
// given; Groups has gone. The console shows this for any workspace;
// "Manage workspace" shows it to a customer's own admins.
// audience: "console" | "customer".
export default function WorkspaceView({ workspaceId, user, audience = "console", onBack, onChanged }) {
  // The caller's latest handler, without reloading when it changes identity.
  const changedRef = useRef(onChanged);
  changedRef.current = onChanged;
  const isStaff = canPlatform(user, "tenants.manage");
  const isSupport = !isStaff && canPlatform(user, "tenants.view");
  // Support looks; bpExperts admins and the workspace's own admins change things.
  const mayChange = !isSupport;

  const [workspace, setWorkspace] = useState(null);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("users");
  // "Make a view" on the Databases tab: the database it should be for. The
  // Views tab opens its New view dialog on it and hands this back, so
  // "add a database, now give people a view of it" is one movement.
  const [newViewFor, setNewViewFor] = useState(null);
  const makeViewFor = (database) => {
    setNewViewFor(database);
    setTab("views");
  };
  // `notice` is the theme's palette, so the toast's own text is `toast`.
  const [toast, setToast] = useState("");
  const [renaming, setRenaming] = useState(false);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [archiving, setArchiving] = useState(false);

  const load = useCallback(async () => {
    const res = await workspacesApi.get(workspaceId);
    if (res.success) {
      setWorkspace(res.content);
      setName(res.content.name);
      setError("");
    } else setError(res.msg || "Couldn't load this workspace.");
  }, [workspaceId]);

  useEffect(() => {
    load();
  }, [load]);

  const changed = useCallback(
    (msg) => {
      load();
      if (changedRef.current) changedRef.current(msg);
    },
    [load]
  );

  const cancelRename = () => {
    setRenaming(false);
    setName(workspace?.name || "");
  };

  const rename = async (event) => {
    if (event) event.preventDefault();
    if (!name.trim() || name.trim() === workspace?.name) {
      cancelRename();
      return;
    }
    setSaving(true);
    const res = await workspacesApi.rename(workspaceId, name.trim());
    setSaving(false);
    if (!res.success) {
      setError(res.msg || "Couldn't rename the workspace.");
      return;
    }
    setRenaming(false);
    setToast(res.msg || "Renamed.");
    changed(res.msg);
  };

  if (error && !workspace) {
    return (
      <Stack spacing={2} sx={{ p: { xs: 2, md: 3 } }}>
        {onBack && (
          <Button startIcon={<ArrowBackRoundedIcon />} onClick={() => onBack()} sx={{ alignSelf: "flex-start" }}>
            All workspaces
          </Button>
        )}
        <Refusal>{error}</Refusal>
      </Stack>
    );
  }

  const seats = workspace?.seats;
  const share = seats?.allowed ? Math.min(100, (seats.used / seats.allowed) * 100) : 0;

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, ...focusRing }}>
      {onBack && (
        <Button startIcon={<ArrowBackRoundedIcon />} onClick={() => onBack()} sx={{ mb: 1.5, ml: -1 }}>
          All workspaces
        </Button>
      )}

      <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "center" }} sx={{ mb: 2.5 }}>
        <Stack direction="row" spacing={1.75} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={{ width: 52, height: 52, borderRadius: 3.5, flexShrink: 0, display: "grid", placeItems: "center", bgcolor: brand.ink, color: brand.signal, fontWeight: 800 }}
          >
            {workspace ? nameInitials(workspace.name) : ""}
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            {!workspace ? (
              <Skeleton width={220} height={36} />
            ) : renaming ? (
              <Stack component="form" onSubmit={rename} direction="row" spacing={1} alignItems="center">
                <TextField
                  size="small"
                  autoFocus
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  onKeyDown={(e) => e.key === "Escape" && cancelRename()}
                  label="Workspace name"
                  sx={{ maxWidth: 360, flex: 1 }}
                />
                <LoadingButton type="submit" variant="contained" loading={saving} disabled={!name.trim()} aria-label="Save name" sx={{ minWidth: 40, px: 1 }}>
                  <CheckRoundedIcon fontSize="small" />
                </LoadingButton>
                <IconButton aria-label="Cancel renaming" onClick={cancelRename}>
                  <CloseRoundedIcon />
                </IconButton>
              </Stack>
            ) : (
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Typography variant="h5" sx={{ fontWeight: 700, overflowWrap: "anywhere" }}>
                  {workspace.name}
                </Typography>
                {mayChange && (
                  <Tooltip title="Rename">
                    <IconButton size="small" onClick={() => setRenaming(true)} aria-label="Rename workspace">
                      <EditRoundedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            )}
            {workspace && (
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                <Chip size="small" label={planLabel(workspace.plan)} sx={{ bgcolor: brand.signal, color: brand.ink, fontWeight: 700 }} />
                {workspace.status && workspace.status !== "active" && (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={STATUS_LABELS[workspace.status] || workspace.status}
                    sx={
                      workspace.status === "trialing"
                        ? undefined
                        : { color: notice.wait.fg, bgcolor: notice.wait.bg, borderColor: notice.wait.line }
                    }
                  />
                )}
                {workspace.noAdmin && <Chip size="small" variant="outlined" label="Run by bpExperts" />}
                <Typography variant="body2" color="text.secondary">
                  {seatText(seats)}
                </Typography>
              </Stack>
            )}
            {seats?.allowed ? (
              <LinearProgress
                variant="determinate"
                value={share}
                sx={{
                  mt: 1,
                  maxWidth: 360,
                  height: 6,
                  borderRadius: 999,
                  bgcolor: brand.canvas,
                  "& .MuiLinearProgress-bar": { bgcolor: seats.used >= seats.allowed ? notice.fix.fg : brand.ink },
                }}
              />
            ) : null}
          </Box>
        </Stack>

        <Stack direction="row" spacing={1.5} alignItems="center">
          {workspace?.admins?.length > 0 && (
            <Tooltip title={`Admins: ${workspace.admins.map((a) => a.name).join(", ")}`}>
              <AvatarGroup max={4} sx={{ "& .MuiAvatar-root": { width: 32, height: 32, fontSize: "0.75rem", bgcolor: brand.inkSoft } }}>
                {workspace.admins.map((admin) => (
                  <Avatar key={admin.id} alt={fullName(admin)}>
                    {initials(admin)}
                  </Avatar>
                ))}
              </AvatarGroup>
            </Tooltip>
          )}
          {audience === "console" && isStaff && workspace && (
            <Button variant="outlined" startIcon={<ArchiveRoundedIcon />} onClick={() => setArchiving(true)} sx={quietDanger}>
              Archive
            </Button>
          )}
        </Stack>
      </Stack>

      {error && workspace && (
        <Refusal sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Refusal>
      )}

      <Tabs
        value={tab}
        onChange={(e, next) => setTab(next)}
        variant="scrollable"
        allowScrollButtonsMobile
        sx={{
          mb: 2.5,
          borderBottom: `1px solid ${brand.line}`,
          "& .MuiTab-root": { textTransform: "none", fontWeight: 600, minHeight: 44 },
          "& .MuiTabs-indicator": { bgcolor: brand.ink, height: 3, borderRadius: 3 },
          "& .Mui-selected": { color: `${brand.ink} !important` },
        }}
      >
        {TABS.map((item) => (
          <Tab key={item.key} value={item.key} label={item.label} />
        ))}
      </Tabs>

      {!workspace ? (
        <Stack spacing={1}>
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} variant="rounded" height={64} sx={{ borderRadius: 3 }} />
          ))}
        </Stack>
      ) : (
        <>
          {tab === "users" && (
            <UsersTab workspace={workspace} user={user} mayChange={mayChange} isStaff={isStaff} notify={setToast} onChanged={() => changed()} />
          )}
          {/* Groups are legacy: nothing reads them for access. An old link
              lands on Views, which is where access is given now. */}
          {tab === "groups" && <ViewsTab workspace={workspace} mayChange={mayChange} notify={setToast} onChanged={() => changed()} />}
          {tab === "views" && (
            <ViewsTab
              workspace={workspace}
              mayChange={mayChange}
              notify={setToast}
              onChanged={() => changed()}
              startNewFor={newViewFor}
              onStartNewHandled={() => setNewViewFor(null)}
            />
          )}
          {tab === "databases" && (
            <DatabasesTab
              workspace={workspace}
              user={user}
              isStaff={isStaff}
              mayChange={mayChange}
              notify={setToast}
              onChanged={() => changed()}
              onMakeView={makeViewFor}
            />
          )}
          {tab === "plan" && <PlanTab workspace={workspace} user={user} notify={setToast} onChanged={() => changed()} />}
          {tab === "api" && <ApiKeysTab workspace={workspace} mayChange={mayChange} isStaff={isStaff} notify={setToast} />}
          {tab === "activity" && <ActivityTab workspace={workspace} />}
        </>
      )}

      <ConfirmDialog
        open={archiving}
        title={`Archive ${workspace?.name}?`}
        body="Its people lose access to its databases at once. Nothing is deleted: people, views, databases and history stay, and you can restore the workspace from Archived."
        onConfirm={async () => {
          const res = await workspacesApi.archive(workspaceId);
          if (res.success) {
            if (changedRef.current) changedRef.current(res.msg);
            if (onBack) onBack(res.msg);
            else load();
          }
          return res;
        }}
        onClose={() => setArchiving(false)}
      />

      <Toast notice={toast} onClose={() => setToast("")} />
    </Box>
  );
}
